import React, { useCallback, useEffect } from 'react';
import {
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
  FlatList,
  ActivityIndicator,
  RefreshControl,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { goBackOrHome } from '../../navigation/goBackOrHome';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { fpColors } from '../../styles/theme';
import {
  loadNotifications,
  markAllRead,
  dismiss,
  selectNotifications,
  selectIsLoadingList,
  selectIsLoadingMore,
  selectHasMore,
  selectListError,
  selectIsMarkingAllRead,
  selectUnreadCount,
} from './notificationsSlice';
import { NotificationCard } from './components/NotificationCard';
import { useNotificationDeepLink } from './useNotificationDeepLink';
import { useNotificationActions } from './useNotificationActions';
// FE-227 v2 — mount the shared DenyNotesSheet at the screen root so
// the [Decline] button on any notification card can open it. The
// hook tracks visibility internally; we just bind the props.
import { DenyNotesSheet } from '../borrowRequests/components/DenyNotesSheet';
// Sprint 8 follow-up — load the /users/shares cache so the
// per-notification state derivation (Claimed / Returned / Declined)
// has data to read from. Dashboard + CrewHub already load it on
// mount; we mirror that here for the deep-link entry point.
import { loadUserShares } from '../sharing/sharingSlice';
import type { NotificationItem } from '../../api/types';
import { FilterRow } from '../../components/FilterRow';

/**
 * NotificationsScreen — Sprint 8 / FE-128.
 *
 * Full inbox for the user's notifications. Sister surface to
 * NotificationBell: the bell shows the unread badge + opens this
 * screen on tap; this screen shows the full list with mark-all-read,
 * pagination, swipe-to-dismiss, and deep-link navigation per row.
 *
 * Navigation contract (deep-link from each card):
 *   - target_type='item'           → ItemDetail({ itemId })
 *   - target_type='group'          → GroupDetail({ groupId })
 *   - target_type='share'          → ShareClaim({ shareToken })  via payload.token
 *   - target_type='borrow_request' → SharingRequests
 *   - target_type='invite'         → AcceptInvite({ token })     via payload.token
 *   - target_type='pack'           → PackDetail({ packId })
 *   - unknown                      → no-op (still marks the row read)
 *
 * The card tap dispatches `markRead(id)` BEFORE navigating, so the
 * badge clears immediately. Server is the source of truth for
 * read state; the slice does an optimistic flip with revert-on-fail.
 */
export const NotificationsScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const dispatch = useAppDispatch();
  const rawItems = useAppSelector(selectNotifications);
  const loading = useAppSelector(selectIsLoadingList);
  const loadingMore = useAppSelector(selectIsLoadingMore);
  const hasMore = useAppSelector(selectHasMore);
  const error = useAppSelector(selectListError);
  const markingAll = useAppSelector(selectIsMarkingAllRead);
  const unreadCount = useAppSelector(selectUnreadCount);

  // Search-UX §3.8 — local filter chrome.  Linear's inbox pattern:
  // a free-text input on top + a chip row for the 3-5 most useful
  // slicing axes (read state + notification family).  All filtering
  // is client-side over the already-paginated set — server-side
  // filter params would be a separate BE change.
  const [searchQuery, setSearchQuery] = React.useState('');
  const [activeChip, setActiveChip] = React.useState<string>('all');
  const items = React.useMemo<NotificationItem[]>(() => {
    const q = searchQuery.trim().toLowerCase();
    return rawItems.filter((n: any) => {
      if (activeChip === 'unread' && n.read_at) return false;
      if (activeChip !== 'all' && activeChip !== 'unread') {
        const t = String(n.type ?? '').toUpperCase();
        if (activeChip === 'borrow' && !t.includes('BORROW')) return false;
        if (activeChip === 'invite' && !t.includes('INVITE')) return false;
        if (activeChip === 'share' && !t.includes('SHARE')) return false;
        if (activeChip === 'custody' && !t.includes('CUSTODY')) return false;
      }
      if (!q) return true;
      // Server pre-renders title/body so we can match the
      // human-facing copy verbatim.
      const title = String(n.title ?? '').toLowerCase();
      const body = String(n.body ?? '').toLowerCase();
      const actor = String(n.actor_name ?? '').toLowerCase();
      return title.includes(q) || body.includes(q) || actor.includes(q);
    });
  }, [rawItems, searchQuery, activeChip]);

  // FE-130b — shared actions hook handles every inline CTA
  // (claim/decline/accept/approve/deny/recall-ack). Same hook is
  // used by Dashboard + CrewHubScreen so per-type behavior stays
  // identical across all three notification surfaces.
  const notifActions = useNotificationActions();

  // Initial load + refresh-on-mount. Even if the bell already
  // populated the cache via polling, we explicitly fetch on mount
  // so the user sees the freshest list (covers the "new device,
  // empty cache" case).
  useEffect(() => {
    dispatch(loadNotifications({ mode: 'replace' }));
    // Sprint 8 follow-up — populate /users/shares so the state chip
    // ("Claimed", "Returned" etc.) on each card renders correctly
    // on cold load. The slice itself de-dupes concurrent dispatches.
    dispatch(loadUserShares({} as any));
  }, [dispatch]);

  const handleRefresh = useCallback(() => {
    dispatch(loadNotifications({ mode: 'replace' }));
  }, [dispatch]);

  const handleLoadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;
    dispatch(loadNotifications({ mode: 'append' }));
  }, [dispatch, loadingMore, hasMore]);

  const handleMarkAll = useCallback(() => {
    if (markingAll || unreadCount === 0) return;
    dispatch(markAllRead());
  }, [dispatch, markingAll, unreadCount]);

  /** Deep-link router. Shared with Dashboard + CrewHubScreen so
   *  tap-behavior stays consistent across every notification surface.
   *  See `useNotificationDeepLink` for the full routing matrix. */
  const handleCardPress = useNotificationDeepLink();

  const handleDismiss = useCallback(
    (n: NotificationItem) => {
      dispatch(dismiss(n.id));
    },
    [dispatch],
  );

  const renderItem = useCallback(
    ({ item }: { item: NotificationItem }) => (
      <NotificationCard
        notification={item}
        onPress={() => handleCardPress(item)}
        onDismiss={() => handleDismiss(item)}
        actions={notifActions.buildActionsFor(item)}
        state={notifActions.deriveStateFor(item)}
      />
    ),
    [handleCardPress, handleDismiss, notifActions],
  );

  return (
    <View style={styles.page}>
      <View style={styles.topBar}>
        <Pressable
          onPress={() => goBackOrHome(navigation)}
          style={styles.backBtn}
          hitSlop={8}
          accessibilityRole="button"
          accessibilityLabel="Go back"
        >
          <MaterialIcons
            name="arrow-back"
            size={22}
            color={fpColors.onSurface}
          />
        </Pressable>
        <Text style={styles.topTitle}>Notifications</Text>
        <Pressable
          onPress={handleMarkAll}
          disabled={markingAll || unreadCount === 0}
          style={({ pressed }) => [
            styles.markAllBtn,
            {
              opacity:
                markingAll || unreadCount === 0 ? 0.4 : pressed ? 0.7 : 1,
            },
          ]}
          accessibilityRole="button"
          accessibilityLabel="Mark all notifications as read"
          testID="notifications-mark-all-read"
        >
          {markingAll ? (
            <ActivityIndicator size="small" color={fpColors.primary} />
          ) : (
            <Text style={styles.markAllText}>Mark all read</Text>
          )}
        </Pressable>
      </View>

      {/* Search-UX §3.8 — filter chrome.  Hidden when zero
          notifications have loaded so the empty-state copy isn't
          cluttered.  Linear-inbox pattern: chips for the most-used
          slicing axes + free-text for the long tail. */}
      {rawItems.length > 0 && (
        <FilterRow
          query={searchQuery}
          onQueryChange={setSearchQuery}
          placeholder="Search notifications"
          chips={[
            { key: 'all', label: 'All' },
            { key: 'unread', label: `Unread${unreadCount > 0 ? ` ${unreadCount}` : ''}` },
            { key: 'borrow', label: 'Borrow' },
            { key: 'invite', label: 'Invite' },
            { key: 'share', label: 'Share' },
            { key: 'custody', label: 'Custody' },
          ]}
          activeKey={activeChip}
          onChipPress={setActiveChip}
          testID="notifications-filter"
        />
      )}

      {loading && items.length === 0 ? (
        <View style={styles.loadingState}>
          <ActivityIndicator size="large" color={fpColors.primary} />
        </View>
      ) : error && items.length === 0 ? (
        <View style={styles.errorState}>
          <MaterialIcons
            name="error-outline"
            size={32}
            color={fpColors.error}
          />
          <Text style={styles.errorTitle}>Couldn’t load notifications</Text>
          <Text style={styles.errorBody}>{error}</Text>
          <Pressable
            onPress={handleRefresh}
            style={styles.retryBtn}
            accessibilityRole="button"
          >
            <Text style={styles.retryText}>Retry</Text>
          </Pressable>
        </View>
      ) : items.length === 0 ? (
        <View style={styles.emptyState}>
          <View style={styles.emptyIconWrap}>
            <MaterialIcons
              name="notifications-none"
              size={36}
              color={fpColors.onSurfaceVariant}
            />
          </View>
          <Text style={styles.emptyTitle}>You’re all caught up</Text>
          <Text style={styles.emptyBody}>
            New activity from your groups, shares, and borrows will
            show up here.
          </Text>
        </View>
      ) : (
        <FlatList
          data={items}
          keyExtractor={(item) => String(item.id)}
          renderItem={renderItem}
          onEndReachedThreshold={0.4}
          onEndReached={handleLoadMore}
          refreshControl={
            <RefreshControl
              refreshing={loading}
              onRefresh={handleRefresh}
              tintColor={fpColors.primary}
            />
          }
          ListFooterComponent={
            loadingMore ? (
              <View style={styles.footerSpinner}>
                <ActivityIndicator
                  size="small"
                  color={fpColors.primary}
                />
              </View>
            ) : null
          }
          contentContainerStyle={{ paddingBottom: 24 }}
        />
      )}
      {/* FE-227 v2 — decline-with-notes modal. Controlled by the hook;
          opens when the user taps Decline on a BORROW_REQUEST_RECEIVED
          card anywhere in the inbox. */}
      <DenyNotesSheet
        visible={notifActions.denySheet.visible}
        request={notifActions.denySheet.request}
        onClose={notifActions.denySheet.onClose}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  page: { flex: 1, backgroundColor: fpColors.background },
  topBar: {
    height: Platform.OS === 'ios' ? 104 : 68,
    paddingTop: Platform.OS === 'ios' ? 48 : 12,
    paddingHorizontal: 12,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: fpColors.surfaceContainerLowest,
    borderBottomWidth: 1,
    borderBottomColor: fpColors.outlineVariant,
  },
  backBtn: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: fpColors.surfaceContainerLow,
    justifyContent: 'center',
    alignItems: 'center',
  },
  topTitle: {
    flex: 1,
    textAlign: 'center',
    fontFamily: 'Inter_700Bold',
    fontSize: 16,
    color: fpColors.onSurface,
  },
  markAllBtn: {
    minWidth: 90,
    height: 36,
    paddingHorizontal: 10,
    borderRadius: 18,
    justifyContent: 'center',
    alignItems: 'center',
  },
  markAllText: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 13,
    color: fpColors.primary,
  },
  loadingState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
  },
  errorTitle: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 16,
    color: fpColors.onSurface,
    marginTop: 12,
  },
  errorBody: {
    fontFamily: 'Inter_400Regular',
    fontSize: 13,
    color: fpColors.onSurfaceVariant,
    marginTop: 6,
    textAlign: 'center',
  },
  retryBtn: {
    marginTop: 16,
    paddingHorizontal: 18,
    paddingVertical: 10,
    borderRadius: 22,
    backgroundColor: fpColors.primaryContainer,
  },
  retryText: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 13,
    color: fpColors.onPrimaryContainer,
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 40,
  },
  emptyIconWrap: {
    width: 72,
    height: 72,
    borderRadius: 36,
    backgroundColor: fpColors.surfaceContainerLow,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 17,
    color: fpColors.onSurface,
    marginTop: 14,
  },
  emptyBody: {
    fontFamily: 'Inter_400Regular',
    fontSize: 13,
    color: fpColors.onSurfaceVariant,
    marginTop: 6,
    textAlign: 'center',
    lineHeight: 19,
  },
  footerSpinner: {
    paddingVertical: 16,
    alignItems: 'center',
  },
});
